import { showErrorMessage } from '../../../../components/message-boxes/ErrorMessage';
import { castSingleColumn } from '../../../../data/init-data/helpers';
import { VALTYPES } from '../../../../data/interfaces';
import { getDataTable } from '../../charts/chartDataBindings';
import { TpDashboardActionContext, TpDbActionStepDefinition as TpDbActionStepTypeDefinition } from '../interfaces';
import { addTableColumn } from '../helpers/derivedColumnPatch';
import { createAddColumnAction, createColumnMetadata } from '../../../../data/mutation-ledger/schemas';

function getJsonSchema(): any {
  return {
    type: 'object',
    title: 'Add Async Column',
    description: 'Add a new column (property) in a datatable by fetching it from storage.',
    properties: {
      type: { type: 'string', const: 'AddAsyncColumn' },
      sourceDataTableId: {
        type: 'string',
        description: 'Identifier of the lazy loading data table that holds the column in storage.',
      },
      columnId: {
        type: 'string',
        description: 'Identifier of the column to load.',
      },
      targetDataTableId: {
        type: 'string',
        description: 'Identifier of the data table to which the column should be added.',
      },
      newColId: {
        type: 'string',
        description: 'Identifier of the new column (same as source column by default).',
      },
      newColName: {
        type: 'string',
        description: 'Name of the new column (taken from source table metadata by default).',
      },
      newColDescription: {
        type: 'string',
        description: 'Description of the new column (taken from source table metadata by default).',
      },
      newColDataType: {
        type: 'string',
        description: 'Data type of the new column to be created (taken from source table metadata by default).',
      },
      decimalDigits: {
        type: 'number',
        description: 'Decimal digit count for numerical columns (taken from source table metadata by default).',
      },
      newGroupId: {
        type: 'string',
        description: 'Identifier of the group for the new column (taken from source table metadata by default).',
      },
      newGroupName: {
        type: 'string',
        description: 'Name of the group for the new column (can be absent if that group already exists).',
      },
      newGroupDescription: {
        type: 'string',
        description: 'Description of the group for the new column (can be absent if that group already exists).',
      },
    },
    required: ['type', 'sourceDataTableId', 'columnId', 'targetDataTableId'],
  };
}

function execute(ctx: TpDashboardActionContext, step: any, onCompleted: () => void) {
  const sourceDataTableId = step.sourceDataTableId as string;
  const columnId = step.columnId as string;
  const targetDataTableId = step.targetDataTableId as string;
  const newColId = (step.newColId ?? columnId) as string;
  const targetDataTable = getDataTable(ctx.dataContext, targetDataTableId);

  // Check if column was already loaded: skip loading
  if (targetDataTable.columns.find((columnInfo) => columnInfo.id === newColId)) {
    onCompleted();
    return;
  }

  // Get the properties for the new column
  const lazyLoadingTable = ctx.dataContext.lazyLoadingTableMetas.find(
    (dataTable) => dataTable.id === sourceDataTableId,
  );
  const columnInfo = lazyLoadingTable ? lazyLoadingTable.columns.find((column) => column.id === columnId) : null;

  const newColName: string = step.newColName ?? columnInfo?.name ?? newColId;
  const newColDescription: string | null = step.newColDescription ?? columnInfo?.description;
  let newColDataType: string = step.newColDataType ?? columnInfo?.valueType ?? 'string';
  if (newColDataType === VALTYPES.MULTISTRING) {
    newColDataType = VALTYPES.MULTICAT;
  }

  const decimalDigits: number | null = step.decimalDigits ?? columnInfo?.decimalDigits ?? null;
  const newGroupId: string | null = step.newGroupId ?? columnInfo?.groupId ?? null;
  let newGroupName: string | null = step.newGroupName ?? null;
  let newGroupDescription: string | null = step.newGroupDescription ?? null;

  if (newGroupName === null && !step.newGroupId && columnInfo?.groupId) {
    newGroupName = lazyLoadingTable?.columnGroups.find((group) => group.id === columnInfo.groupId)?.name ?? null;
  }
  if (newGroupDescription === null && !step.newGroupId && columnInfo?.groupId) {
    newGroupDescription =
      lazyLoadingTable?.columnGroups.find((group) => group.id === columnInfo.groupId)?.description ?? null;
  }

  ctx.dashboardContext.getAsyncTableColumn(sourceDataTableId, columnId).then((newColData) => {
    // Convert the string column to the dataType
    newColData = castSingleColumn(newColData, newColDataType);

    if (newColData.length !== targetDataTable.columnData[targetDataTable.colItemId].length) {
      showErrorMessage(`The source and target data have different row counts.`);
      onCompleted();
      return;
    }

    const ledgerAction = createAddColumnAction({
      targetTableId: targetDataTableId,
      targetColumnIds: [newColId],
      payload: {
        type: 'ASYNC_COLUMN',
        sourceDataTableId,
        columnId,
        columnMetadata: createColumnMetadata({
          name: newColName,
          description: newColDescription,
          valueType: newColDataType as any,
          groupId: newGroupId,
          groupName: newGroupName,
          groupDescription: newGroupDescription,
          decimalDigits,
        }),
      },
    });

    addTableColumn(
      ctx,
      targetDataTableId,
      newColId,
      newColName,
      newColDescription,
      newColDataType,
      newGroupId,
      newGroupName,
      newGroupDescription,
      newColData,
      decimalDigits,
      ledgerAction,
    );
    onCompleted();
  });
}

const dbActionStepTypeAddAsyncColumn: TpDbActionStepTypeDefinition = {
  stepType: 'AddAsyncColumn',
  execute,
  getJsonSchema,
};

export default dbActionStepTypeAddAsyncColumn;
